import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputLabel,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ErrorPage from "../Components/ErrorPage";
import PayrollAdd from "./PayrollAdd";
import PayrollEdit from "./PayrollEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";

const PayrollIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, index) => currentYear - index);
  const monthOptions = [
    { value: 1, label: t("payroll.january", "January") },
    { value: 2, label: t("payroll.february", "February") },
    { value: 3, label: t("payroll.march", "March") },
    { value: 4, label: t("payroll.april", "April") },
    { value: 5, label: t("payroll.may", "May") },
    { value: 6, label: t("payroll.june", "June") },
    { value: 7, label: t("payroll.july", "July") },
    { value: 8, label: t("payroll.august", "August") },
    { value: 9, label: t("payroll.september", "September") },
    { value: 10, label: t("payroll.october", "October") },
    { value: 11, label: t("payroll.november", "November") },
    { value: 12, label: t("payroll.december", "December") },
  ];

  // Pagination and filtering states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  // Fetch payroll records with search and pagination.
  const payrollQuery = React.useMemo(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (selectedEmployee) params.set("employee", selectedEmployee);
    if (selectedYear) params.set("year", selectedYear);
    if (selectedMonth) params.set("month", selectedMonth);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    params.set("page", String(page));
    params.set("page_size", String(rowsPerPage));
    return params.toString();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    startDate,
    endDate,
    page,
    rowsPerPage,
  ]);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "payroll",
      searchTerm,
      selectedEmployee,
      selectedYear,
      selectedMonth,
      startDate,
      endDate,
      page,
      rowsPerPage,
    ],
    `shop/payrolls/?${payrollQuery}`,
    token
  );

  // Fetch employees for dropdown filter
  const { data: employeesData } = useFetchObjects(
    ["employees"],
    "shop/employees/?page_size=1000",
    token
  );

  // Delete hook
  const { deleteObject } = useDelete();

  // Handle delete function
  const handleDelete = (id) => {
    setConfirmDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await deleteObject(`shop/payrolls/${confirmDialog.id}/`, token);
      refetch();
      setConfirmDialog({ open: false, id: null });
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // Print function for individual payroll
  const handlePrintPayroll = (payroll) => {
    const printWindow = window.open('', '_blank');
    const formattedNow = new Date().toLocaleString();
    const formattedPaymentDate = payroll.payment_date
      ? new Date(payroll.payment_date).toLocaleString()
      : "N/A";
    const printContent = `
      <html>
        <head>
          <title>Payroll Print</title>
          <style>
            @media print {
              @page { margin: 10mm; size: A4 portrait; }
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              background: #f5f7fb;
              color: #132238;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 14px;
            }
            .page {
              width: 100%;
              min-height: 100vh;
              background: #f5f7fb;
            }
            .topbar {
              height: 56px;
              background: #1976d2;
              color: #fff;
              display: flex;
              align-items: center;
              padding: 0 22px;
              box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
            }
            .topbar-title {
              font-size: 18px;
              font-weight: 700;
              letter-spacing: -0.01em;
            }
            .content {
              padding: 28px 26px 24px;
            }
            .header-row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
              margin-bottom: 18px;
            }
            .title {
              margin: 0 0 10px;
              font-size: 28px;
              font-weight: 500;
              color: #132238;
            }
            .subtitle {
              margin: 0;
              color: #5f6f86;
              font-size: 14px;
            }
            .action-chip {
              background: #1976d2;
              color: #fff;
              padding: 12px 18px;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 700;
              box-shadow: 0 6px 18px rgba(25, 118, 210, 0.22);
              white-space: nowrap;
            }
            .filters {
              display: grid;
              grid-template-columns: 1.6fr 1fr 1fr 1fr;
              gap: 14px;
              margin-bottom: 18px;
            }
            .filter-box {
              background: #fff;
              border: 1px solid #d7e1ee;
              border-radius: 8px;
              min-height: 64px;
              padding: 12px 16px;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }
            .filter-label {
              color: #7b8798;
              font-size: 12px;
              margin-bottom: 4px;
            }
            .filter-value {
              color: #132238;
              font-size: 16px;
              font-weight: 500;
            }
            .table-card {
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 10px;
              overflow: hidden;
              box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            thead tr {
              background: #1976d2;
            }
            th {
              color: #fff;
              text-align: left;
              font-size: 14px;
              font-weight: 700;
              padding: 18px 16px;
            }
            td {
              padding: 18px 16px;
              border-bottom: 1px solid #e7edf5;
              color: #132238;
              vertical-align: top;
            }
            tr:last-child td {
              border-bottom: none;
            }
            .summary-row {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 14px;
              margin-top: 18px;
            }
            .summary-card {
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 8px;
              padding: 14px 16px;
            }
            .summary-title {
              color: #7b8798;
              font-size: 12px;
              margin-bottom: 6px;
            }
            .summary-value {
              color: #132238;
              font-size: 18px;
              font-weight: 700;
            }
            .notes-card {
              margin-top: 18px;
              background: #fff;
              border: 1px solid #dbe3ef;
              border-radius: 8px;
              padding: 16px;
            }
            .notes-title {
              font-size: 14px;
              font-weight: 700;
              margin-bottom: 8px;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              color: #7b8798;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="topbar">
              <div class="topbar-title">Professional Dashboard</div>
            </div>
            <div class="content">
              <div class="header-row">
                <div>
                  <h1 class="title">Payroll List</h1>
                  <p class="subtitle">Data loaded on ${formattedNow}</p>
                </div>
                <div class="action-chip">PAYROLL DETAILS</div>
              </div>

              <div class="filters">
                <div class="filter-box">
                  <div class="filter-label">Employee</div>
                  <div class="filter-value">${payroll?.employee_details?.name || 'N/A'}</div>
                </div>
                <div class="filter-box">
                  <div class="filter-label">Shop</div>
                  <div class="filter-value">${payroll?.shop_details?.name || 'N/A'}</div>
                </div>
                <div class="filter-box">
                  <div class="filter-label">Year</div>
                  <div class="filter-value">${payroll.year || 'N/A'}</div>
                </div>
                <div class="filter-box">
                  <div class="filter-label">Month</div>
                  <div class="filter-value">${payroll.month || 'N/A'}</div>
                </div>
              </div>

              <div class="table-card">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Shop</th>
                      <th>Employee</th>
                      <th>Amount</th>
                      <th>Currency</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Payment Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>${payroll.id || 'N/A'}</td>
                      <td>${payroll?.shop_details?.name || 'N/A'}</td>
                      <td>${payroll?.employee_details?.name || 'N/A'}</td>
                      <td>${payroll.amount || '0.00'}</td>
                      <td>${payroll?.currency_details?.title || 'N/A'}</td>
                      <td>${payroll.year || 'N/A'}</td>
                      <td>${payroll.month || 'N/A'}</td>
                      <td>${formattedPaymentDate}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="summary-row">
                <div class="summary-card">
                  <div class="summary-title">Amount</div>
                  <div class="summary-value">${payroll.amount || '0.00'}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Currency</div>
                  <div class="summary-value">${payroll?.currency_details?.title || 'N/A'}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Payment Date</div>
                  <div class="summary-value" style="font-size: 15px;">${formattedPaymentDate}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-title">Record Status</div>
                  <div class="summary-value" style="color: #1976d2;">Payroll</div>
                </div>
              </div>

              <div class="notes-card">
                <div class="notes-title">Notes</div>
                <div>${payroll.notes || 'No notes available for this payroll record.'}</div>
              </div>

              <div class="footer">Generated from the payroll dashboard print view</div>
            </div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    refetch();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    startDate,
    endDate,
    page,
    rowsPerPage,
    refetch,
  ]);

  // Determine payroll list and total count (using DRF paginated response)
  const payrolls = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : payrolls.length;

  if (isError) {
    return <ErrorPage message={t("payroll.failedToLoadPayrolls")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedEmployee("");
    setSelectedYear("");
    setSelectedMonth("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f5f7fb",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 1.5,
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: "#132238",
            letterSpacing: "-0.02em",
          }}
        >
          {t("payroll.payrollList")}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpenAdd(true)}
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            minWidth: 142,
            height: 42,
            px: 2.5,
            borderRadius: 1.5,
            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.22)",
            fontWeight: 700,
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          {t("payroll.addPayroll")}
        </Button>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{ color: "#5f6f86", mb: 2.5, fontWeight: 400 }}
      >
        {t("payroll.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search and Filter Fields */}
      <Box
        sx={{
          mb: 2,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 1fr) 220px 140px 150px 170px 170px auto",
          },
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("payroll.searchPayrolls")}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#6f7d90", fontSize: 28 }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
        >
          <Select
            value={selectedEmployee}
            onChange={handleFilterChange(setSelectedEmployee)}
            displayEmpty
            placeholder={t("payroll.selectEmployee")}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {(employeesData?.results || employeesData || []).map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <Select
            value={selectedYear}
            onChange={handleFilterChange(setSelectedYear)}
            displayEmpty
          >
            <MenuItem value="">{t("payroll.year", "Year")}</MenuItem>
            {Array.from({ length: 31 }, (_, i) => 2020 + i).map((year) => (
              <MenuItem key={year} value={String(year)}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <Select
            value={selectedMonth}
            onChange={handleFilterChange(setSelectedMonth)}
            displayEmpty
          >
            <MenuItem value="">{t("payroll.month", "Month")}</MenuItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <MenuItem key={month} value={String(month).padStart(2, "0")}>
                {new Date(0, month - 1).toLocaleString("default", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={t("ReportIndex.datepicker_start", "Start Date")}
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label={t("ReportIndex.datepicker_end", "End Date")}
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <Button variant="outlined" onClick={clearFilters}>
          {t("ReportIndex.btn_clear", "Clear")}
        </Button>
      </Box>

      {/* Payroll Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          borderRadius: 1.5,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
          border: "1px solid #dbe3ef",
          backgroundColor: "#fff",
        }}
      >
        <Table sx={{ minWidth: 750 }} aria-label="payroll table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.payrollId")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.shop")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.employee")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.amount")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.currency")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.year")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.month")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.paymentDate")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.notes")}
              </TableCell>
              <TableCell
                sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
              >
                {t("payroll.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Shop",
                      "Employee",
                      "Amount",
                      "Currency",
                      "Year",
                      "Month",
                      "Payment Date",
                      "Notes",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payrolls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {t("payroll.noPayrollsFound", "No payrolls found")}
                    </TableCell>
                  </TableRow>
                ) : payrolls.map((payroll, index) => (
                  <TableRow key={payroll.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll?.shop_details?.name || t("payroll.unknownShop")}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll?.employee_details?.name ||
                        t("payroll.unknownEmployee")}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll.amount || "--"}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll?.currency_details?.title || "--"}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll.year || "--"}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll.month || "--"}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll.payment_date
                        ? new Date(payroll.payment_date).toLocaleString()
                        : "--"}
                    </TableCell>
                    <TableCell sx={{ color: "#132238" }}>
                      {payroll.notes || "--"}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            minWidth: 122,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => {
                            setEditData(payroll);
                            setOpenEdit(true);
                          }}
                        >
                          {t("payroll.editPayroll")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<PrintIcon />}
                          sx={{
                            minWidth: 92,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handlePrintPayroll(payroll)}
                        >
                          Print
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          sx={{
                            minWidth: 78,
                            fontWeight: 700,
                            borderRadius: 1,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleDelete(payroll.id)}
                        >
                          {t("payroll.delete")}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="18px 10px 6px"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ color: "#132238", fontSize: 16 }}>
            {t("payroll.rowsPerPage", "Rows Per Page")}
          </Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={30}>30</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
          page={Math.min(page, Math.max(1, Math.ceil(totalCount / rowsPerPage)))}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          disabled={totalCount === 0}
        />
      </Grid>

      {openAdd && (
        <PayrollAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <PayrollEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
      >
        <DialogTitle>{t("payroll.confirmDelete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("payroll.confirmDeleteMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: null })}>
            {t("payroll.cancel")}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t("payroll.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollIndex;
